import { useEffect, useState } from "react";
import "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";
import RootLoading from "./components/RootLoading/RootLoading";
import Router from "./router/Router";
import { IRootState } from ".";

import Profile from "./utils/apiServices/profile";
import Address from "./utils/apiServices/address";
import { login, logout } from "./features/login/reducer";
import { setProfile } from "./features/profile/reducer";
import notify from "./utils/notify";
import { generateToken, zim } from "./utils/chat";
import { setChat } from "./features/chat/reducer";
import { isRTL, Language, getLanguageFromCountry } from "./i18n/translations";
import { clearCart } from "./features/cart/reducer";
import { setLanguage } from "./features/language/reducer";

function App() {
  const events = [
    "load",
    "mousemove",
    "mousedown",
    "click",
    "scroll",
    "keypress",
  ];

  let timer: any;

  const handleLogoutTimer = () => {
    timer = setTimeout(() => {
      // clears any pending timer.
      resetTimer();
      // Listener clean up. Removes the existing event listener from the window
      Object.values(events).forEach((item) => {
        window.removeEventListener(item, resetTimer);
      });
      // logs out user
      dispatch(logout());
      dispatch(clearCart());
    }, 6000000); // 10000ms = 10secs. You can change the time.
  };

  // this resets the timer if it exists.
  const resetTimer = () => {
    if (timer) clearTimeout(timer);
  };

  useEffect(() => {
    Object.values(events).forEach((item) => {
      window.addEventListener(item, () => {
        resetTimer();
        handleLogoutTimer();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch();
  const user = useSelector((state: IRootState) => state.user.value);
  const language = useSelector((state: IRootState) => state.language.value);
  const { loggedIn } = user;
  const [rootLoading, setRootLoading] = useState(true);
  // access token
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    "";

  // Get Profile
  useEffect(() => {
    if (accessToken && !loggedIn) {
      Address.getAddress()
        .then((res) => {
          if (res.success) {
            dispatch(
              setProfile({
                address: res.data,
                addressLoading: false,
              })
            );
          } else {
            dispatch(setProfile({ addressLoading: false }));
          }
        })
        .catch(() => {
          dispatch(setProfile({ addressLoading: false }));

        });
      Profile.getProfile()
        .then((res) => {
          if (res.success) {
            const { data } = res;
            const localToken = localStorage.getItem("accessToken");
            // Dispatch Login
            dispatch(
              login({
                id: data.id,
                firstname: data.firstname,
                country: data.country,
                lastname: data.lastname,
                phone: data.phone,
                email: data.email,
                image: data.image,
                is_phone_verified: data.is_phone_verified,
                email_verified_at: data.email_verified_at,
                interest: data.interest,
                device_token: data.device_token,
                status: data.status,
                user_id: data.user_id,
                order_count: data.order_count,
                login_medium: data.login_medium,
                social_id: data.social_id,
                zone_id: data.zone_id,
                wallet_balance: data.wallet_balance,
                ref_code: data.ref_code,
                ref_by: data.ref_by,
                temp_token: data.temp_token,
                account_status: data.account_status,
                created_at: data.created_at,
                updated_at: data.updated_at,
                delete_at: data.delete_at,
                access_token: accessToken,
                loggedIn: true,
                remember_me: !!localToken,
                sms_consent_at: data.sms_consent_at,
              })
            );
            const userId = data.user_id.toString();
            const userName = `${data.firstname + " " + data.lastname}`;
            const newUserDetails = { userName, userID: userId };
            zim
              .login(newUserDetails, generateToken(userId, 0))
              .then(function () {
                dispatch(
                  setChat({
                    isLogged: true,
                    isLoading: false,
                  })
                );
              });

            setRootLoading(false);

            if (!data.is_phone_verified) {
              window.location.assign(
                `/resend-phone-verification?email=${encodeURIComponent(data.email)}&phone=${encodeURIComponent(data.phone || "")}`
              );
            }
          } else {
            setRootLoading(false);
            // notify("error", `${res.message}`);
          }
        })
        .catch(() => {
          setRootLoading(false);
        });
    } else {
      setRootLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  // IP-based language detection when user has no saved preference
  // Uses fallback chain: ipapi.co → ipinfo.io (ipapi.co is often blocked in China)
  useEffect(() => {
    if (localStorage.getItem("language")) return;

    const tryDetectCountry = (): Promise<string | null> => {
      return fetch("https://ipapi.co/json/")
        .then((res) => res.json())
        .then((data) => data.country_code || null)
        .catch(() =>
          fetch("https://ipinfo.io/json")
            .then((res) => res.json())
            .then((data) => data.country || null)
            .catch(() => null)
        );
    };

    tryDetectCountry().then((countryCode) => {
      const detectedLang = countryCode ? getLanguageFromCountry(countryCode) : null;
      if (detectedLang) {
        dispatch(setLanguage({ selected: detectedLang }));
      }
    });
  }, [dispatch]);

  // Apply RTL direction when Arabic is selected
  useEffect(() => {
    const currentLang = (language?.selected as Language) || "en";
    const htmlElement = document.documentElement;
    
    if (isRTL(currentLang)) {
      htmlElement.setAttribute("dir", "rtl");
      htmlElement.setAttribute("lang", currentLang);
    } else {
      htmlElement.setAttribute("dir", "ltr");
      htmlElement.setAttribute("lang", currentLang);
    }
  }, [language?.selected]);

  return <div className="">{rootLoading ? <RootLoading /> : <Router />}</div>;
}

export default App;
